import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import CardView from '../../combat/ui/CardView.tsx';
import loreCornerDetail from '../../assets/lore/lore_details.png';
import loreButton from '../../assets/lore/lore_button.png';

function CornerDetail({ className = '', rotationClass = '' }) {
  return (
    <img
      src={loreCornerDetail}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none absolute h-[52px] w-[52px] select-none opacity-80 ${rotationClass} ${className}`}
    />
  );
}

function FooterButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative h-[68px] w-[250px] appearance-none border-0 bg-transparent p-0 text-white shadow-none outline-none transition hover:opacity-90 active:scale-[0.99]"
      style={{ fontFamily: 'Karma, serif' }}
    >
      <img
        src={loreButton}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
      />
      <span className="relative z-[1] flex h-full w-full items-center justify-center text-[18px] font-semibold translate-y-[2px]">
        {label}
      </span>
    </button>
  );
}

export default function DeckCullRewardModal({
  open,
  deckCards,
  deckCullCards,
  getDeckCullBlockReason,
  onClaim,
  onSkip,
}) {
  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[6100] flex items-center justify-center overflow-hidden p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <motion.div
            className="relative z-[1] w-[980px] max-w-[94vw] bg-black/80 px-6 pb-8 pt-5 text-white shadow-[0_20px_60px_rgba(0,0,0,0.72)] sm:px-8 sm:pb-10 sm:pt-6"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ fontFamily: 'Karma, serif' }}
          >
            <CornerDetail className="left-4 top-4" rotationClass="rotate-180" />
            <CornerDetail className="right-4 top-4" rotationClass="-rotate-90" />
            <CornerDetail className="left-4 bottom-4" rotationClass="rotate-90" />
            <CornerDetail className="right-4 bottom-4" />

            <div className="relative z-[1] mx-auto flex max-h-[82svh] w-full max-w-[900px] flex-col items-center pt-6 sm:pt-8">
              <h2 className="text-center text-[18px] font-semibold text-white sm:text-[20px]">
                Floor Cleansed
              </h2>

              <div className="mt-3 text-center text-[18px] leading-[1.25] text-white/90">
                You've earned a chance to forget.
              </div>

              <div className="mt-1 text-center text-[14px] leading-[1.2] text-white/60">
                Minimum deck size: 16
              </div>

              {!Array.isArray(deckCards) ? (
                <div className="mt-6 text-center text-[18px] text-white/70">Loading deck…</div>
              ) : (
                <div
                  className="mt-6 min-h-0 w-full flex-1 overflow-y-auto pr-1 overscroll-contain"
                  style={{ touchAction: 'pan-y' }}
                  onWheelCapture={(e) => e.stopPropagation()}
                  onTouchStartCapture={(e) => e.stopPropagation()}
                  onTouchMoveCapture={(e) => e.stopPropagation()}
                >
                  <div className="rewardRemoveGrid">
                    {deckCullCards
                      .slice()
                      .sort((a, b) =>
                        a.kind === b.kind
                          ? String(a.title || a.name || '').localeCompare(
                              String(b.title || b.name || '')
                            )
                          : a.kind === 'physical'
                            ? -1
                            : 1
                      )
                      .map((c) => {
                        const blockReason = getDeckCullBlockReason(c);
                        const disabled = !!blockReason;

                        return (
                          <div
                            key={c.id}
                            className={disabled ? 'rewardRemoveCard disabled' : 'rewardRemoveCard'}
                            title={blockReason || 'Click to remove this card'}
                            role="button"
                            tabIndex={disabled ? -1 : 0}
                            onClick={() => {
                              if (!disabled) onClaim?.(c.id);
                            }}
                            onKeyDown={(e) => {
                              if (disabled) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onClaim?.(c.id);
                              }
                            }}
                          >
                            <CardView card={c} />
                            {disabled && (
                              <div
                                style={{
                                  marginTop: 8,
                                  fontSize: 12,
                                  color: 'rgba(252, 211, 77, 0.9)',
                                  textAlign: 'center',
                                }}
                              >
                                {blockReason}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <div className="mt-6 flex w-full justify-center">
                <FooterButton label="Skip" onClick={onSkip} />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
